import MuiBottomNavigation from "@mui/material/BottomNavigation";
import BottomNavigationAction from "@mui/material/BottomNavigationAction";
import SpeedDial from "@mui/material/SpeedDial";
import SpeedDialAction from "@mui/material/SpeedDialAction";
import { BookMarked, BookOpen, Ellipsis, Folder } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { generatePath, useHistory, useLocation } from "react-router-dom";
import routing from "routing";
import { isFaqEnabled } from "src/hooks/useAppConfig";
import useSearchParams from "src/hooks/useMenuSection";
import CoursesIcon from "src/images/Icons/newIcons/navigation/courses.svg";
import ExamIcon from "src/images/Icons/newIcons/navigation/exams.svg";
import FlashcardIcon from "src/images/Icons/newIcons/navigation/flashcards.svg";
import HomeIcon from "src/images/Icons/newIcons/navigation/home.svg";

import { isLdek, isLek, isLekEn } from "../../helpers/services";
import useBottomNavigationParams from "../../hooks/useBottomNavigationParams";

function BottomNavigation() {
	const { t } = useTranslation();
	const history = useHistory();
	const { getHistoryPath } = useBottomNavigationParams();
	const value = useSearchParams();
	const { pathname } = useLocation();
	const [open, setOpen] = useState(false);

	useEffect(() => {
		const unlisten = history.listen(() => setOpen(false));
		return unlisten;
	}, [history]);

	const moreMenuItems = [
		...(isLek() || isLdek()
			? [
					{
						label: t("navbar.articles", "Baza wiedzy"),
						icon: <BookOpen className="icon-small" />,
						navIcon: <BookOpen className="icon-big size-12" />,
						path: routing.paths.knowledgeBase,
						testId: "bottom-nav-articles-link",
					},
				]
			: []),
		{
			label: t("navbar.folders", "Foldery"),
			icon: <Folder className="icon-small" />,
			navIcon: <Folder className="icon-big size-12" />,
			path: routing.paths.foldersList,
			testId: "bottom-nav-folders-link",
		},
		...(isFaqEnabled()
			? [
					{
						label: t("navbar.guide", "Poradnik"),
						icon: <BookMarked className="icon-small" />,
						navIcon: <BookMarked className="icon-big size-12" />,
						path: generatePath(routing.paths.guide),
						testId: "bottom-nav-guide-link",
					},
				]
			: []),
	];

	const activeMoreItem = moreMenuItems.find((item) =>
		pathname.startsWith(item.path),
	);

	const showMoreMenu = isLek() || isLdek() || isLekEn();

	return (
		<>
			<MuiBottomNavigation
				className="bottom-navigation"
				showLabels
				value={value}
				onChange={(_event, newValue: string) => {
					if (newValue === "more") return;
					history.push(getHistoryPath(newValue));
				}}
			>
				<BottomNavigationAction
					className="bottom-navigation-action"
					label={t("navbar.homeMobile")}
					icon={<HomeIcon className="icon-big size-12" />}
					value={routing.paths.home}
				/>
				<BottomNavigationAction
					className="bottom-navigation-action"
					label={t("navbar.courses", "Kursy")}
					icon={<CoursesIcon className="icon-big size-12" />}
					value={routing.paths.coursesList}
				/>
				<BottomNavigationAction
					className="bottom-navigation-action"
					label={t("navbar.exams")}
					icon={<ExamIcon className="icon-big size-12" />}
					value={routing.paths.exams}
					data-test-id="bottom-nav-exams-link"
				/>
				<BottomNavigationAction
					className="bottom-navigation-action"
					label={t("navbar.flashcards")}
					icon={<FlashcardIcon className="icon-big size-12" />}
					value={routing.paths.flashcards}
				/>
				{showMoreMenu && (
					<BottomNavigationAction
						className={`bottom-navigation-action ${open || activeMoreItem ? "Mui-selected" : ""}`}
						label={
							activeMoreItem ? activeMoreItem.label : t("navbar.more", "Więcej")
						}
						icon={
							activeMoreItem ? (
								activeMoreItem.navIcon
							) : (
								<Ellipsis className="icon-big size-12" />
							)
						}
						value="more"
						onClick={() => setOpen((prev) => !prev)}
						data-test-id="bottom-nav-more-button"
					/>
				)}
			</MuiBottomNavigation>
			{showMoreMenu && (
				<>
					<SpeedDial
						ariaLabel="more menu"
						className="bottom-navigation-speed-dial"
						open={open}
						icon={<Ellipsis />}
						hidden
					>
						{moreMenuItems.map((item) => (
							<SpeedDialAction
								key={item.path}
								icon={item.icon}
								className="bottom-navigation-speed-action"
								slotProps={{
									tooltip: { title: item.label, open: true },
									staticTooltipLabel: {
										className: "bottom-navigation-speed-tooltip",
									},
									fab: { className: "bottom-navigation-speed-fab" },
								}}
								onClick={() => {
									setOpen(false);
									history.push(getHistoryPath(item.path));
								}}
							/>
						))}
					</SpeedDial>
					{open && (
						<div
							className="bottom-navigation-backdrop"
							onClick={() => setOpen(false)}
						/>
					)}
				</>
			)}
		</>
	);
}

export default BottomNavigation;
